import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  TemplateRef,
  ViewContainerRef,
  Output,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import defaults from 'lodash-es/defaults';
import isUndefined from 'lodash-es/isUndefined';
import isNumber from 'lodash-es/isNumber';
import flattenDeep from 'lodash-es/flattenDeep';

import { IFBDropdownOptions } from '@app/models/dropdown';

@Directive({
  selector: '[appDropdown]',
  standalone: false
})
export class DropdownDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('appDropdown') templateRef: TemplateRef<any>;
  @Input('appDropdownOptions') options: IFBDropdownOptions = {};

  @Output() shown = new EventEmitter<void>();
  @Output() hidden = new EventEmitter<void>();

  private overlayRef: OverlayRef;
  private isOpen = false;

  constructor(
    private elementRef: ElementRef,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!isUndefined(changes.options?.currentValue?.isOpen)) {
      if (changes.options.currentValue.isOpen) {
        this.show();
      } else if (this.isOpen) {
        this.hide();
      }
    }
  }

  ngOnDestroy() {
    this.hide();
  }

  ngAfterViewInit() {
    this.elementRef.nativeElement.classList.add('app-dropdown_trigger');

    defaults(this.options, {
      closeOnSelect: true,
      fullWidth: false,
      maxHeight: 200,
      width: 250
    });

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top'
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom'
        },
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top'
        }
      ])
      .withPush(false);

    this.overlayRef = this.overlay.create({
      scrollStrategy: this.overlay.scrollStrategies.close(),
      positionStrategy,
      width: this.options.width,
      height: this.options.height,
      maxHeight: isNumber(this.options.maxHeight) ? this.options.maxHeight : null,
      panelClass: ['app-dropdown', ...flattenDeep([this.options.panelClass])]
    });

    this.overlayRef.detachments().subscribe(() => {
      // Update internal state when Overlay closed by its own behaviour
      this.isOpen = false;
      this.elementRef.nativeElement.classList.remove('open');
      this.hidden.emit();
    });

    this.overlayRef.attachments().subscribe(() => {
      this.isOpen = true;
      this.elementRef.nativeElement.classList.add('open');
      this.shown.emit();
    });

    this.overlayRef.outsidePointerEvents().subscribe((event: MouseEvent) => {
      // triggerElement is the element where [appDropdown] was used
      const triggerElement = (event.target as HTMLElement).closest('.app-dropdown_trigger');

      // Click outside but ignore trigger element
      if (triggerElement !== this.elementRef.nativeElement) {
        this.hide();
      }
    }, null);

    this.overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      // Multi-browser support for Esc key
      if (event.key === 'Escape' || event.keyCode === 27) {
        this.hide();
      }
    }, null);

    this.overlayRef.overlayElement.addEventListener('click', () => {
      if (this.options.closeOnSelect) {
        this.hide();
      }
    });
  }

  @HostListener('click')
  toggle() {
    // For controlled dropdowns trigger is ignored
    if (!isUndefined(this.options.isOpen)) {
      return;
    }

    if (this.isOpen) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    if (this.elementRef.nativeElement.children?.[0]?.disabled) {
      return;
    }

    // Before every dropdown open if fullWidth is true, get the actual trigger element width and update the overlay
    if (this.options.fullWidth && this.options.offsetWidth) {
      this.overlayRef.updateSize({
        width: this.elementRef.nativeElement.offsetWidth + this.options.offsetWidth
      });
    } else if (this.options.fullWidth) {
      this.overlayRef.updateSize({
        width: this.elementRef.nativeElement.offsetWidth
      });
    }

    this.overlayRef.attach(new TemplatePortal(this.templateRef, this.viewContainerRef));
  }

  hide() {
    if (!this.isOpen) {
      return;
    }

    this.overlayRef.detach();
  }
}
